import { useCallback, useEffect, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'

const HISTORY_PAGE = 60

// Chat manager for /english/tutor. History lives in
// english_tutor_messages — the edge function appends both the user
// and assistant rows server-side; the frontend keeps a local mirror
// so the UI feels instant.
//
// Returns:
//   - messages   — ordered ASC by created_at
//   - sending    — true while the edge function is in flight
//   - error      — last error string, if any
//   - send(text) — append + call english-tutor-chat
//   - reset()    — delete every message for the user (with confirm UX on caller side)
export function useEnglishTutor() {
  const { user } = useAuth()
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)

  const fetch = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    setError(null)
    const { data, error: err } = await supabase
      .from('english_tutor_messages')
      .select('id, role, content, model, created_at')
      .order('created_at', { ascending: false })
      .limit(HISTORY_PAGE)
    if (err) {
      setError(err.message)
      setLoading(false)
      return
    }
    setMessages((data || []).slice().reverse())
    setLoading(false)
  }, [user])

  useEffect(() => { fetch() }, [fetch])

  const send = useCallback(async (text) => {
    const trimmed = (text || '').trim()
    if (!trimmed || sending) return { error: 'empty' }
    setError(null)
    // Optimistic append of the user message with a temp id so it
    // renders immediately. The edge function persists the real row
    // and returns the assistant reply — we then re-pull the
    // tail-end of history so both rows reflect their server ids.
    const tempId = `tmp:${Date.now()}`
    setMessages((prev) => [...prev, { id: tempId, role: 'user', content: trimmed, created_at: new Date().toISOString() }])
    setSending(true)
    try {
      const { data, error: err } = await supabase.functions.invoke('english-tutor-chat', {
        body: { user_message: trimmed },
      })
      if (err) throw new Error(err.message || 'send failed')
      if (data?.error) throw new Error(data.error)
      if (!data?.message) throw new Error('empty response')
      // Re-pull to swap the temp row for the persisted one and to
      // pick up the assistant's row in order. Cheap — single index
      // hit on (user_id, created_at desc).
      const { data: refreshed } = await supabase
        .from('english_tutor_messages')
        .select('id, role, content, model, created_at')
        .order('created_at', { ascending: false })
        .limit(HISTORY_PAGE)
      setMessages((refreshed || []).slice().reverse())
      return { ok: true, provider: data.provider }
    } catch (e) {
      setError(e?.message || 'send failed')
      // Drop the optimistic row so the user can edit + retry.
      setMessages((prev) => prev.filter((m) => m.id !== tempId))
      return { error: e?.message || 'send failed' }
    } finally {
      setSending(false)
    }
  }, [sending])

  const reset = useCallback(async () => {
    if (!user) return { error: 'not signed in' }
    const snapshot = messages
    setMessages([])
    const { error: err } = await supabase
      .from('english_tutor_messages')
      .delete()
      .eq('user_id', user.id)
    if (err) {
      setMessages(snapshot)
      return { error: err.message }
    }
    return { ok: true }
  }, [user, messages])

  return { messages, loading, sending, error, send, reset, refetch: fetch }
}
